import { cn } from '@/lib/utils'
import type { EvaluationTemplate } from '@/lib/types'

export interface RadarAxis {
  label: string
  /** This run's score for the area, or null if nothing on it was scored. */
  now: number | null
  /** The comparison run's score, when one is selected. */
  before?: number | null
}

/**
 * A maturity radar across every knowledge area of an evaluation.
 *
 * WHAT A RADAR IS AND ISN'T GOOD FOR. It reads "which areas are strong and which
 * are weak, all at once", and with two runs plotted it shows movement as one
 * shape growing. What it cannot support is precise comparison: the axes are in an
 * arbitrary order, so rotating them changes the outline without changing a single
 * number, and the area enclosed grows with the SQUARE of the scores, which
 * flatters improvement. So the numbers are on the chart, the rings are labelled,
 * and both pages that use this keep a table alongside — the radar is the summary,
 * not the evidence. For actually comparing two runs area by area, the dumbbell on
 * the Evaluations page is the better instrument, and it stays the default view.
 *
 * `scale_min` sits at the centre, which is the convention for a maturity radar
 * and the reason every ring carries its number: without them, a team scoring 1
 * across the board would read as "no data" rather than "Initial everywhere".
 *
 * Hand-built SVG at a fixed size rather than a chart library, for the same
 * reasons as the dumbbell: the marks inherit the theme's own tokens, and a
 * document that gets printed to PDF can't wait for a container measurement.
 */
export function RadarChart({
  axes,
  template,
  nowLabel,
  beforeLabel,
  hideLegend = false,
  className,
}: {
  axes: RadarAxis[]
  template: Pick<EvaluationTemplate, 'scale_min' | 'scale_max'>
  /** Series name for this run — required, since one series carries no legend. */
  nowLabel: string
  /** Series name for the comparison. Its presence is what draws the legend. */
  beforeLabel?: string | null
  /**
   * Suppress the built-in legend when the caller already renders one for the
   * same two series — the monthly report puts this chart directly above the
   * per-area dumbbell, and two identical legends is noise, not redundancy.
   */
  hideLegend?: boolean
  className?: string
}) {
  const min = template.scale_min
  const max = template.scale_max
  const n = axes.length
  if (n < 3) return null

  // Room for labels outside the plot: the longest area name is "Engineering
  // Process and Project Management", which wraps to three lines.
  // maxR is set from the room the LABELS need, not the other way round: the
  // longest wrapped line is ~18 characters at 9.5px (~85px), plus the 16px gap,
  // so 145 leaves a comfortable margin inside a 520-wide box and stops the plot
  // sitting in the middle of it looking undersized.
  const W = 520
  const H = 440
  const cx = W / 2
  const cy = H / 2 + 6
  const maxR = 145

  const angle = (i: number) => (-90 + (360 / n) * i) * (Math.PI / 180)
  const radius = (v: number) => ((v - min) / (max - min)) * maxR
  const at = (i: number, r: number): [number, number] => [
    cx + r * Math.cos(angle(i)),
    cy + r * Math.sin(angle(i)),
  ]

  // One ring per scale point up to five of them, then every other point. A 1–5
  // maturity template is unchanged; the 0–10 Team AI Skills template would
  // otherwise draw eleven labelled rings 13px apart, which reads as hatching
  // rather than as a scale.
  const ringStep = Math.max(1, Math.ceil((max - min) / 5))
  const rings: number[] = []
  for (let v = min; v <= max; v += ringStep) rings.push(v)
  if (rings[rings.length - 1] !== max) rings.push(max)
  const hasBefore = axes.some((a) => a.before != null) && Boolean(beforeLabel)

  // A missing score would otherwise collapse that spoke to the centre and read
  // as "scored 1". Drop the vertex instead; the polygon closes over the gap and
  // the axis label says "not scored".
  const polygon = (pick: (a: RadarAxis) => number | null | undefined) =>
    axes
      .map((a, i) => {
        const v = pick(a)
        return v == null ? null : at(i, radius(v)).map((c) => c.toFixed(1)).join(',')
      })
      .filter(Boolean)
      .join(' ')

  const summary = axes
    .map((a) => `${a.label}: ${a.now == null ? 'not scored' : a.now.toFixed(1)}`)
    .join('; ')

  return (
    <div className={cn('min-w-0', className)}>
      {/* Two series share the plot, so identity is never carried by colour
          alone — the legend names both, as on the dumbbell. */}
      {hasBefore && !hideLegend && (
        <div className="mb-1 flex flex-wrap items-center justify-center gap-4 text-xs">
          <span className="inline-flex items-center gap-1.5">
            <span
              className="inline-block h-2.5 w-2.5 rounded-full"
              style={{ background: 'var(--chart-before)' }}
            />
            {beforeLabel}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span
              className="inline-block h-2.5 w-2.5 rounded-full"
              style={{ background: 'var(--chart-after)' }}
            />
            {nowLabel}
          </span>
        </div>
      )}

      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="h-auto w-full"
        role="img"
        aria-label={`Maturity by knowledge area, scale ${min} to ${max}. ${summary}`}
      >
        {/* Grid: recessive, and it must stay behind the marks. */}
        <g fill="none" stroke="hsl(var(--chart-grid))" strokeWidth={1}>
          {rings.map((v) => (
            <polygon
              key={v}
              points={axes
                .map((_, i) => at(i, radius(v)).map((c) => c.toFixed(1)).join(','))
                .join(' ')}
            />
          ))}
          {axes.map((_, i) => {
            const [x, y] = at(i, maxR)
            return <line key={i} x1={cx} y1={cy} x2={x} y2={y} />
          })}
        </g>

        {/* Ring values, on the vertical spoke. Without these the centre reads as
            zero and the whole chart overstates how bad a low score is. */}
        <g
          fill="hsl(var(--muted-foreground))"
          fontSize={9}
          textAnchor="middle"
          className="tabular-nums"
        >
          {rings.map((v) => (
            <text key={v} x={cx} y={cy - radius(v) - 2}>
              {v}
            </text>
          ))}
        </g>

        {hasBefore && (
          <polygon
            points={polygon((a) => a.before)}
            fill="var(--chart-before)"
            fillOpacity={0.14}
            stroke="var(--chart-before)"
            strokeWidth={2}
            strokeLinejoin="round"
          />
        )}

        <polygon
          points={polygon((a) => a.now)}
          fill="var(--chart-after)"
          fillOpacity={0.16}
          stroke="var(--chart-after)"
          strokeWidth={2}
          strokeLinejoin="round"
        />

        {/* Vertices last, ringed in the surface colour so they stay legible where
            the two polygons cross. */}
        {hasBefore &&
          axes.map((a, i) =>
            a.before == null ? null : (
              <circle
                key={`b-${i}`}
                cx={at(i, radius(a.before))[0]}
                cy={at(i, radius(a.before))[1]}
                r={3.5}
                fill="var(--chart-before)"
                stroke="hsl(var(--card))"
                strokeWidth={1.5}
              >
                <title>{`${a.label} — ${beforeLabel}: ${a.before.toFixed(1)}`}</title>
              </circle>
            ),
          )}
        {axes.map((a, i) =>
          a.now == null ? null : (
            <circle
              key={`n-${i}`}
              cx={at(i, radius(a.now))[0]}
              cy={at(i, radius(a.now))[1]}
              r={4}
              fill="var(--chart-after)"
              stroke="hsl(var(--card))"
              strokeWidth={1.5}
            >
              <title>{`${a.label} — ${nowLabel}: ${a.now.toFixed(1)}`}</title>
            </circle>
          ),
        )}

        {/* Axis labels, in TEXT tokens rather than the series colour, with the
            value spelled out — a radar you have to eyeball against a ring is a
            picture, not a reading. */}
        {axes.map((a, i) => {
          const [lx, ly] = at(i, maxR + 16)
          const cos = Math.cos(angle(i))
          const sin = Math.sin(angle(i))
          const anchor = cos > 0.25 ? 'start' : cos < -0.25 ? 'end' : 'middle'
          const lines = wrap(a.label, 18)
          // Nudge a label that sits above or below the plot clear of it.
          const dy = sin < -0.9 ? -lines.length * 10 + 4 : sin > 0.9 ? 6 : -((lines.length - 1) * 5)
          return (
            <g key={`l-${i}`} textAnchor={anchor}>
              {lines.map((line, k) => (
                <text
                  key={k}
                  x={lx}
                  y={ly + dy + k * 10}
                  fontSize={9.5}
                  fill="hsl(var(--muted-foreground))"
                >
                  {line}
                </text>
              ))}
              <text
                x={lx}
                y={ly + dy + lines.length * 10}
                fontSize={10}
                fontWeight={600}
                fill="hsl(var(--foreground))"
                className="tabular-nums"
              >
                {a.now == null ? 'not scored' : a.now.toFixed(1)}
              </text>
            </g>
          )
        })}
      </svg>
    </div>
  )
}

/** Greedy word wrap to a character budget, for SVG text that can't reflow. */
function wrap(text: string, max: number): string[] {
  const out: string[] = []
  let line = ''
  for (const word of text.split(/\s+/)) {
    if (line && (line + ' ' + word).length > max) {
      out.push(line)
      line = word
    } else {
      line = line ? `${line} ${word}` : word
    }
  }
  if (line) out.push(line)
  return out
}
